/**
 * A VPN user's own sign-in lifetime setting (doc 28 §4.10): how often this
 * user must sign in to Secure Access again, and how long each access token
 * lives. It wins over the device group's and the tenant's values. Shown on the
 * VPN user page with what applies — per Access group the user's devices are
 * in, since a device's group is part of its answer.
 */
import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import accessService, { type SignInLevel, type UserSignInPolicy } from '../../services/access.service';
import { useAuthStore } from '../../stores/authStore';
import { USER_TYPES } from '../../utils/constants';
import { useToast } from '../common/ToastContainer';
import SignInLifetimeFields from './SignInLifetimeFields';
import { describeValue, effectiveAt, signInLevelError } from '../../utils/signInPolicy';

const UserSignInPolicyCard = ({ userId }: { userId: string }) => {
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  const canEdit = currentUser?.type === USER_TYPES.SUPER_ADMIN || currentUser?.type === USER_TYPES.TENANT_ADMIN;
  const [view, setView] = useState<UserSignInPolicy | null>(null);
  const [draft, setDraft] = useState<SignInLevel>({ reauthInterval: null, tokenLifetime: null });
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;
    accessService
      .getUserSignInPolicy(userId)
      .then((v) => {
        if (cancelled) return;
        setView(v);
        setDraft(v.user);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (failed) {
    return <p className="text-sm text-gray-500" data-testid="user-sign-in-unavailable">Sign-in lifetime: could not be read.</p>;
  }
  if (!view) return null;

  // What this user inherits where they set nothing: their default Access
  // group's value, else the tenant's, else the default.
  const inheritedLevel = view.defaultGroup ?? { reauthInterval: null, tokenLifetime: null };
  const tenantEffective = effectiveAt(view.tenant, {
    reauthInterval: view.defaults.reauthInterval,
    reauthIntervalSource: 'default',
    tokenLifetime: view.defaults.tokenLifetime,
    tokenLifetimeSource: 'default',
  }, 'tenant');
  const inherited = effectiveAt(inheritedLevel, tenantEffective, 'group');
  const dirty = draft.reauthInterval !== view.user.reauthInterval || draft.tokenLifetime !== view.user.tokenLifetime;

  const save = async () => {
    const problem = signInLevelError(draft, view);
    if (problem) {
      toast.error('Invalid value', problem);
      return;
    }
    setSaving(true);
    try {
      const next = await accessService.setUserSignInPolicy(userId, draft);
      setView(next);
      setDraft(next.user);
      setEpoch((n) => n + 1);
      toast.success('Sign-in setting saved', `Policy bundle ${next.compile.version} published to the tenant's gateways`);
    } catch (err) {
      toast.error('Could not save', err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid="user-sign-in-policy">
      <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <Clock className="h-4 w-4 text-gray-400" />
          Sign-in lifetime (Secure Access)
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          This user&apos;s own setting wins over their device group&apos;s and the tenant&apos;s.
          {view.defaultGroup ? ` Inherited from their default group "${view.defaultGroup.name}" where it sets a value.` : ''}
        </p>
      </div>
      <SignInLifetimeFields
        key={`${userId}-${epoch}`}
        value={draft}
        onChange={setDraft}
        inherited={inherited}
        level="user"
        limits={view}
        inheritLabel="Group / tenant value"
        disabled={!canEdit || saving}
        testIdPrefix="user"
      />
      {view.byGroup.length > 0 && (
        <div className="text-xs text-gray-600" data-testid="user-sign-in-by-group">
          <span className="font-medium">Applies to this user&apos;s devices:</span>
          <ul className="mt-1 space-y-0.5">
            {view.byGroup.map((g) => (
              <li key={g.groupId}>
                {g.groupName}: sign in again every {describeValue(g.effective.reauthInterval, g.effective.reauthIntervalSource)}
                {' · '}token lifetime {describeValue(g.effective.tokenLifetime, g.effective.tokenLifetimeSource)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {canEdit && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-primary px-3 py-1 text-xs"
            onClick={() => void save()}
            disabled={!dirty || saving}
            data-testid="user-sign-in-save"
          >
            {saving ? 'Publishing…' : 'Save'}
          </button>
          {dirty && (
            <button
              type="button"
              className="btn-secondary px-3 py-1 text-xs"
              onClick={() => {
                setDraft(view.user);
                setEpoch((n) => n + 1);
              }}
              disabled={saving}
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default UserSignInPolicyCard;
